import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Leaf, FlaskConical, MessageCircle, Building2, Star, Send } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const HERO_IMG = "https://images.unsplash.com/photo-1587049352851-8d4e89133924?w=1600&q=80";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [best, setBest] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data.slice(0, 8))).catch(() => {});
    api.get("/products?etiket=cok-satan&limit=4").then(({ data }) => setBest(data.items)).catch(() => {});
    api.get("/products?etiket=yeni&limit=4").then(({ data }) => setFresh(data.items)).catch(() => {});
    api.get("/products?etiket=kampanya&limit=4").then(({ data }) => setCampaign(data.items)).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
    api.get("/reviews/featured").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  const wa = settings?.whatsapp;
  const waHero = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Merhaba, ürünleriniz hakkında bilgi almak istiyorum.")}` : "#";
  const t = settings?.texts || {};
  const today = new Date().toISOString().slice(0, 10);
  const campImg = settings?.hero_campaign_image;
  const campActive = campImg && (!settings?.hero_campaign_start || today >= settings.hero_campaign_start) && (!settings?.hero_campaign_end || today <= settings.hero_campaign_end);
  const heroSrc = campActive ? campImg : (settings?.hero_image || HERO_IMG);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      trackEvent("newsletter_signup", { location: "ana_sayfa" });
      toast.success("Bültenimize kaydoldunuz! Hoş geldin kuponu: HOSGELDIN10");
      setEmail("");
    } catch { toast.error("Kayıt başarısız, tekrar deneyin"); }
  };

  const SectionTitle = ({ children, sub }) => (
    <div className="mb-8">
      <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-forest">{children}</h2>
      {sub && <p className="text-stone-500 mt-2 text-sm sm:text-base">{sub}</p>}
    </div>
  );

  return (
    <div data-testid="home-page">
      <section className="relative hero-grain overflow-hidden">
        <img src={heroSrc} alt="Doğal bal" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(27,67,50,0.85) 0%, rgba(27,67,50,0.55) 60%, rgba(27,67,50,0.92) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-36">
          <p className="reveal-up text-gold-light text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold">{t.hero_eyebrow || "Anadolu'nun Bereketli Topraklarından"}</p>
          <h1 className="reveal-up font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mt-4 leading-tight max-w-2xl" style={{ animationDelay: "0.1s" }}>
            {(t.hero_title || "Doğal Lezzetler,\nGüvenilir Alışveriş").split("\n").map((line, i, arr) => (<span key={i}>{line}{i < arr.length - 1 && <br />}</span>))}
          </h1>
          <p className="reveal-up text-cream/80 mt-5 max-w-xl text-base sm:text-lg leading-relaxed" style={{ animationDelay: "0.2s" }}>
            {t.hero_subtitle || "Katkısız bal, taş değirmen tahin, doğal sirke ve yöresel lezzetler; laboratuvar analizli, özenli paketleme ile kapınıza gelsin."}
          </p>
          <div className="reveal-up flex flex-wrap gap-4 mt-9" style={{ animationDelay: "0.3s" }}>
            <Link to="/urunler" data-testid="hero-explore-button" className="bg-gold hover:bg-gold-dark text-stone-900 font-semibold px-7 py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2">
              {t.hero_cta1 || "Ürünleri Keşfet"} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={waHero} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button" className="bg-[#25D366] hover:bg-[#20ba5a] text-white font-semibold px-7 py-3.5 rounded-xl transition-all shadow-md flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> {t.hero_cta2 || "WhatsApp'tan Bilgi Al"}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 -mt-10 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            [Leaf, "%100 Doğal", "Katkısız ve koruyucusuz ürünler"],
            [FlaskConical, "Analiz Raporlu", "Laboratuvar testli bal ve yağlar"],
            [Truck, "Hızlı Kargo", "24-48 saatte kapınızda"],
            [ShieldCheck, "Güvenli Ödeme", "SSL ve iade garantisi"],
          ].map(([Icon, title, desc], i) => (
            <div key={title} className="bg-white rounded-2xl border border-stone-200/80 p-4 sm:p-5 shadow-[0_4px_20px_-4px_rgba(27,67,50,0.1)] reveal-up" style={{ animationDelay: `${0.1 * i}s` }}>
              <Icon className="w-6 h-6 text-gold" />
              <p className="font-semibold text-stone-900 mt-2 text-sm sm:text-base">{title}</p>
              <p className="text-xs text-stone-500 mt-1">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 mt-20">
        <SectionTitle sub={t.sec_categories_sub || "Aradığınız doğal lezzeti kategorisinde bulun"}>{t.sec_categories_title || "Kategoriler"}</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {categories.map((c) => (
            <Link key={c.category_id} to={`/urunler?kategori=${c.slug}`} data-testid={`home-category-${c.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[4/3] shadow-sm">
              <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/20 to-transparent" />
              <div className="absolute bottom-0 p-4">
                <p className="text-cream font-semibold">{c.name}</p>
                <p className="text-cream/70 text-xs mt-0.5">{c.product_count} ürün · İncele</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 mt-20">
        <div className="flex items-end justify-between mb-8">
          <SectionTitle sub={t.sec_best_sub || "Müşterilerimizin en çok tercih ettikleri"}>{t.sec_best_title || "Çok Satanlar"}</SectionTitle>
          <Link to="/urunler?etiket=cok-satan" className="hidden sm:flex items-center gap-1 text-forest font-semibold text-sm hover:gap-2 transition-all mb-8">Tümü <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {best.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {campaign.length > 0 && (
        <section className="mt-20 bg-cream-dark/60 border-y border-stone-200/60">
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="bg-terra text-white text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5">Kampanya</span>
              <h2 className="font-serif text-2xl sm:text-3xl text-forest">{t.sec_campaign_title || "Fırsat Ürünleri"}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {campaign.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 mt-20">
        <SectionTitle sub={t.sec_new_sub || "Yeni gelen doğal ürünler"}>{t.sec_new_title || "Yeni Ürünler"}</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {fresh.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 mt-20">
        <div className="relative rounded-3xl overflow-hidden bg-forest p-10 sm:p-16 hero-grain">
          <div className="relative max-w-xl">
            <Building2 className="w-10 h-10 text-gold" />
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-4">{t.b2b_title || "Restoran, Otel ve Şarküteriler İçin Kurumsal Tedarik"}</h2>
            <p className="text-cream/75 mt-4 leading-relaxed">{t.b2b_text || "Özel toptan fiyatlar, iskonto oranları, toplu sipariş ve teklif sistemi ile B2B çözümlerimizden yararlanın."}</p>
            <Link to="/b2b" data-testid="home-b2b-cta" className="inline-flex items-center gap-2 mt-7 bg-gold hover:bg-gold-dark text-stone-900 font-semibold px-7 py-3.5 rounded-xl transition-all">
              {t.b2b_cta || "B2B Başvurusu Yap"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 mt-20">
          <SectionTitle sub={t.sec_reviews_sub || "Doğrulanmış müşteri deneyimleri"}>{t.sec_reviews_title || "Müşteri Yorumları"}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {reviews.slice(0, 3).map((r) => (
              <div key={r.review_id} data-testid={`review-${r.review_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "fill-gold text-gold" : "text-stone-300"}`} />)}
                </div>
                <p className="text-stone-700 text-sm mt-3 leading-relaxed">"{r.comment}"</p>
                <p className="text-forest font-semibold text-sm mt-4">{r.name}</p>
                <p className="text-xs text-stone-400">Doğrulanmış Alışveriş</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 mt-20">
          <div className="flex items-end justify-between mb-8">
            <SectionTitle sub={t.sec_blog_sub || "Doğal yaşam ve beslenme üzerine"}>{t.sec_blog_title || "Blog"}</SectionTitle>
            <Link to="/blog" className="hidden sm:flex items-center gap-1 text-forest font-semibold text-sm hover:gap-2 transition-all mb-8">Tüm Yazılar <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.post_id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group bg-white rounded-2xl border border-stone-200/80 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <span className="text-[11px] uppercase tracking-widest text-gold-dark font-semibold">{p.category}</span>
                  <h3 className="font-semibold text-stone-900 mt-2 leading-snug group-hover:text-forest transition-colors">{p.title}</h3>
                  <p className="text-sm text-stone-500 mt-2 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 mt-20">
        <div className="rounded-3xl bg-gradient-to-br from-cream-dark to-cream border border-stone-200/70 p-10 sm:p-14 text-center">
          <Send className="w-8 h-8 text-gold mx-auto" />
          <h2 className="font-serif text-2xl sm:text-3xl text-forest mt-4">{t.newsletter_title || "Bültenimize Katılın"}</h2>
          <p className="text-stone-600 mt-2 text-sm sm:text-base">{t.newsletter_text || "Kaydolun, HOSGELDIN10 koduyla ilk siparişinizde %10 indirim kazanın."}</p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mt-6">
            <input
              data-testid="newsletter-email-input"
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="E-posta adresiniz"
              className="flex-1 h-12 px-4 rounded-xl border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 text-sm"
            />
            <button data-testid="newsletter-submit" className="bg-forest hover:bg-forest-dark text-white font-semibold px-7 py-3 rounded-xl transition-colors">{t.newsletter_button || "Abone Ol"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
